import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, mergeMap } from 'rxjs';
import {
  consumePendingLanguageChanged,
  type PendingLanguageChanged,
} from '../app.i18n';
import * as fromAuth from '../auth/store/auth.actions';
import { AnalyticsService } from './analytics.service';

/**
 * Effects for analytics-specific logic that isn't tied to other domain reducers.
 * Ticket 8: Handles the language_changed event marker consumption (D1).
 * Listens to authenticateUserSuccess (when actor is reliably in the store post-reload).
 * Consumes the localStorage marker, fires sendLanguageChangedEvent only if present + valid.
 */
export class AnalyticsEffects {
  private readonly actions$ = inject(Actions);
  private readonly analyticsService = inject(AnalyticsService);

  /**
   * Consumes the pending language change marker on authenticateUserSuccess (Ticket 8, D1).
   * Fires authenticateUserSuccess on every app boot, not only post-language-change.
   * The marker check makes all other boots a no-op (marker absent).
   * No new actions, no reducer changes; purely fire-and-forget analytics.
   * Effect is dispatch: false (non-blocking, Sentry captures errors).
   */
  languageChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuth.AuthActions.authenticateUserSuccess),
        map(() => consumePendingLanguageChanged()),
        filter(
          (pending): pending is PendingLanguageChanged => pending !== null,
        ),
        mergeMap((pending) =>
          this.analyticsService.sendLanguageChangedEvent(pending),
        ),
      ),
    { dispatch: false },
  );
}
